(() => {
  if (customElements.get('dut-delivery-estimate')) return;

  class DutDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.country = this.form.elements.country;
      this.province = this.form.elements.province;
      this.postcode = this.form.elements.zip;
      this.status = this.querySelector('[data-status]');
      this.rates = this.querySelector('[data-rates]');
      this.note = this.querySelector('[data-result-note]');
      this.button = this.form.querySelector('button');
      this.requestId = 0;
      this.eventController = new AbortController();
      const { signal } = this.eventController;
      const countryNames = new Set();
      Array.from(this.country.options).forEach((option) => {
        if (!['Australia', 'New Zealand'].includes(option.value) || countryNames.has(option.value)) option.remove();
        else countryNames.add(option.value);
      });
      this.country.value = this.dataset.country === 'NZ' ? 'New Zealand' : 'Australia';
      this.updateProvinces();
      this.form.hidden = false;
      this.form.addEventListener('submit', (event) => this.submit(event), { signal });
      this.form.addEventListener('input', () => this.invalidate(), { signal });
      this.form.addEventListener('change', (event) => {
        if (event.target === this.country) this.updateProvinces();
        this.invalidate();
      }, { signal });
      document.addEventListener('change', (event) => {
        if (event.target.closest('cart-items, cart-drawer-items')) this.invalidate('Your cart changed. Check delivery again for an updated estimate.');
      }, { signal });
      document.addEventListener('click', (event) => {
        if (event.target.closest('cart-remove-button')) this.invalidate('Your cart changed. Check delivery again for an updated estimate.');
      }, { signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.invalidate('Your cart changed. Check delivery again for an updated estimate.'));
      }
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.hasQuote) this.invalidate('Check delivery again to confirm the rates for your current cart.');
      }, { signal });
      const openFromLink = () => {
        if (window.location.hash !== `#${this.id}`) return;
        this.querySelector('details').open = true;
        requestAnimationFrame(() => window.dutScrollTo(this));
      };
      window.addEventListener('hashchange', openFromLink, { signal });
      openFromLink();
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.controller?.abort();
      this.unsubscribe?.();
    }

    updateProvinces() {
      let provinces = [];
      try { provinces = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) { /* Shopify supplies this list. */ }
      this.province.replaceChildren(new Option('Select', ''));
      provinces.forEach(([value, name]) => this.province.add(new Option(name, value)));
      this.province.required = provinces.length > 0;
      this.province.disabled = provinces.length === 0;
      this.querySelector('[data-province-field]').hidden = provinces.length === 0;
      this.querySelector('[data-province-label]').textContent = this.country.value === 'Australia' ? 'State or territory' : 'Region';
    }

    invalidate(message = 'Details changed. Check delivery again for an updated estimate.') {
      const hadRequest = this.hasQuote || this.busy;
      this.requestId += 1;
      this.controller?.abort();
      this.busy = false;
      this.hasQuote = false;
      this.button.disabled = false;
      this.form.removeAttribute('aria-busy');
      this.rates.replaceChildren();
      this.rates.hidden = true;
      this.note.hidden = true;
      this.status.textContent = hadRequest ? message : '';
    }

    fingerprint(cart) {
      return JSON.stringify([cart.currency, cart.total_price, cart.total_weight, cart.cart_level_discount_applications, cart.items.map((item) => [item.key, item.quantity, item.final_line_price])]);
    }

    async request(url, signal, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal, headers: { Accept: 'application/json', ...options.headers } });
      if (!response.ok) {
        if (response.status === 422) throw new Error('Check your country, state or region and postcode, then try again.');
        throw new Error('We couldn’t check delivery just now. Try again, or see the available options at checkout.');
      }
      const body = await response.text();
      return body.trim() ? JSON.parse(body) : null;
    }

    async submit(event) {
      event.preventDefault();
      if (!this.form.reportValidity()) return;
      this.invalidate();
      const id = this.requestId;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const controller = this.controller;
      const timeout = setTimeout(() => controller.abort(), 20000);
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams();
      params.set('shipping_address[country]', this.country.value);
      params.set('shipping_address[province]', this.province.value);
      params.set('shipping_address[zip]', this.postcode.value.trim());
      const destination = `${this.postcode.value.trim()}, ${this.country.value}`;
      this.busy = true;
      this.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Checking available delivery options…';
      try {
        const cart = await this.request(`${root}cart.js`, signal);
        if (signal.aborted || id !== this.requestId) return;
        if (!cart.item_count) {
          this.status.textContent = 'Add a product to your cart to check delivery.';
          return;
        }
        if (!cart.requires_shipping) {
          this.status.textContent = 'The items currently in your cart do not require shipping.';
          return;
        }
        const initialCart = this.fingerprint(cart);
        await this.request(`${root}cart/prepare_shipping_rates.json?${params}`, signal, { method: 'POST' });
        let data = null;
        for (let attempt = 0; attempt < 12; attempt += 1) {
          if (signal.aborted || id !== this.requestId) return;
          data = await this.request(`${root}cart/async_shipping_rates.json?${params}`, signal);
          if (data !== null) break;
          await new Promise((resolve) => setTimeout(resolve, 900));
        }
        if (data === null || !Array.isArray(data.shipping_rates)) throw new Error('Delivery is taking longer to calculate. Try again, or check the options at checkout.');
        const latestCart = await this.request(`${root}cart.js`, signal);
        if (signal.aborted || id !== this.requestId) return;
        if (initialCart !== this.fingerprint(latestCart)) {
          this.status.textContent = 'Your cart changed. Check delivery again for an updated estimate.';
          return;
        }
        this.showRates(data.shipping_rates, cart.currency || this.dataset.currency, destination);
      } catch (error) {
        if (id !== this.requestId) return;
        this.status.textContent = error.name === 'AbortError'
          ? 'Delivery is taking longer to calculate. Try again, or check the options at checkout.'
          : (error instanceof SyntaxError || error instanceof TypeError ? 'We couldn’t check delivery just now. Please try again or check at checkout.' : error.message);
      } finally {
        clearTimeout(timeout);
        if (id === this.requestId) {
          this.busy = false;
          this.button.disabled = false;
          this.form.removeAttribute('aria-busy');
        }
      }
    }

    showRates(rates, currency, destination) {
      this.hasQuote = true;
      const validRates = rates.filter((rate) => typeof rate.price === 'string' && rate.price.trim() !== '' && Number.isFinite(Number(rate.price)) && Number(rate.price) >= 0);
      if (!validRates.length) {
        this.status.textContent = 'No delivery options were returned for this postcode. Check the details or contact us for help.';
        return;
      }
      this.status.textContent = `Estimated shipping to ${destination}`;
      validRates.forEach((rate) => {
        const row = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('strong');
        name.textContent = rate.presentment_name || rate.name || 'Delivery';
        const code = /^[A-Z]{3}$/.test(rate.currency || '') ? rate.currency : currency;
        price.textContent = Number(rate.price) === 0 ? 'Free' : new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: code, currencyDisplay: 'code' }).format(Number(rate.price));
        row.append(name, price);
        const dates = (Array.isArray(rate.delivery_range) && rate.delivery_range.length ? rate.delivery_range : [rate.delivery_date]).filter((date) => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date));
        if (dates.length) {
          const estimate = document.createElement('small');
          const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
          estimate.textContent = `Estimated arrival: ${[...new Set(dates)].map((date) => formatter.format(new Date(`${date}T12:00:00Z`))).join(' to ')}`;
          row.append(estimate);
        }
        this.rates.append(row);
      });
      this.rates.hidden = false;
      this.note.hidden = false;
    }
  }

  customElements.define('dut-delivery-estimate', DutDeliveryEstimate);
})();
